import type { ReactElement, ReactNode } from "react";
import { palette, radius, space } from "@ux/tokens";
import { Button } from "../primitives/Button";
import { Stack } from "../primitives/Stack";
import { Text } from "../primitives/Text";

export interface ConfirmActionProps {
    readonly open: boolean;
    readonly title: string;
    readonly description: ReactNode;
    readonly confirmLabel: string;
    readonly cancelLabel?: string;
    readonly destructive?: boolean;
    readonly onConfirm: () => void;
    readonly onCancel: () => void;
    readonly busy?: boolean;
}

export function ConfirmAction({
    open,
    title,
    description,
    confirmLabel,
    cancelLabel = "Cancel",
    destructive = false,
    onConfirm,
    onCancel,
    busy = false,
}: ConfirmActionProps): ReactElement | null {
    if (!open) {
        return null;
    }

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-label={title}
            style={{
                position: "fixed",
                inset: 0,
                background: "rgba(0,0,0,0.55)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: space[4],
                zIndex: 50,
            }}
            onClick={(e) => {
                if (e.target === e.currentTarget) onCancel();
            }}
        >
            <div
                style={{
                    background: palette.canvas,
                    border: `1px solid ${palette.border}`,
                    borderRadius: radius.lg,
                    padding: space[6],
                    maxWidth: 440,
                    width: "100%",
                }}
            >
                <Stack gap={3}>
                    <Text role="heading">{title}</Text>
                    <Text role="muted">{description}</Text>
                    <Stack direction="row" gap={2} justify="flex-end">
                        <Button variant="secondary" onClick={onCancel} disabled={busy}>
                            {cancelLabel}
                        </Button>
                        <Button
                            variant={destructive ? "danger" : "primary"}
                            onClick={onConfirm}
                            disabled={busy}
                        >
                            {busy ? "Working…" : confirmLabel}
                        </Button>
                    </Stack>
                </Stack>
            </div>
        </div>
    );
}
